import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { useSession } from '../hooks/useSession';
import { relativeTime, shortAddress } from '../lib/format';
import { EscrowBanner } from '../components/EscrowMeter';

export function Thread() {
  const { id = '' } = useParams();
  const { address } = useSession();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['thread', id],
    queryFn: () => api.thread(id),
    refetchInterval: 20_000,
  });

  const reply = useMutation({
    mutationFn: (text: string) => api.reply(id, text),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['thread', id] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });

  if (isLoading || !data) return <div className="page page-narrow" />;

  // The opening escrow, if any, governs the whole conversation's framing.
  const openingEscrow = data.messages.find((m) => m.escrow)?.escrow ?? null;
  const openingSender = data.messages.find((m) => m.escrow)?.senderAddress;
  const viewerIsRecipient = openingSender !== address;

  return (
    <div className="page page-narrow">
      <div className="page-head">
        <h2>{data.counterpartyDisplayName ?? shortAddress(data.counterpartyAddress, 6, 6)}</h2>
        <span className="addr" title={data.counterpartyAddress}>
          {shortAddress(data.counterpartyAddress, 6, 6)} · {data.counterpartyChain}
        </span>
      </div>

      {openingEscrow && (
        <div style={{ marginBottom: 20 }}>
          <EscrowBanner escrow={openingEscrow} viewerIsRecipient={viewerIsRecipient} />
        </div>
      )}

      <div className="msgs">
        {data.messages.map((m) => {
          const mine = m.senderAddress === address;
          return (
            <article key={m.id} className="msg">
              <div className="msg-head">
                <span className={`msg-who${mine ? ' is-me' : ''}`}>
                  {mine ? 'You' : (data.counterpartyDisplayName ?? shortAddress(m.senderAddress))}
                </span>
                <span className="msg-time">{relativeTime(m.createdAt)}</span>
              </div>
              <div className="msg-body">{m.body}</div>
            </article>
          );
        })}
      </div>

      <div className="card" style={{ padding: 18 }}>
        <label className="label" htmlFor="reply">
          Your reply
        </label>
        <textarea
          id="reply"
          className="field"
          value={draft}
          placeholder="Write back…"
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="row" style={{ marginTop: 12, gap: 12 }}>
          {viewerIsRecipient && openingEscrow?.status === 'PENDING' && (
            <span className="hint">Sending this releases the escrow to your wallet.</span>
          )}
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={!draft.trim() || reply.isPending}
            onClick={() => reply.mutate(draft.trim())}
          >
            {reply.isPending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
        {reply.isError && (
          <p className="hint hint-warn" style={{ marginBottom: 0 }}>
            {(reply.error as Error).message}
          </p>
        )}
      </div>
    </div>
  );
}
